import React, { memo } from 'react';
import { Typography } from '@mui/material';
import type { DoctorShift } from '../../../../../types/doctorShift';
import {
  TimelineRow as StyledTimelineRow,
  DoctorNameColumn,
  ShiftArea,
} from '../../MonthlyDoctorsShift.styles';
import { ShiftBar } from './ShiftBar';
import type { TimelinePosition } from '../../hooks/useTimelinePosition';

/**
 * TimelineRow Props
 */
interface TimelineRowProps {
  /** 日付ラベル（例: "1/13(月)"） */
  dateLabel: string;
  /** 日付文字列（yyyy-MM-dd形式） */
  dateString: string;
  /** 該当日付のシフト一覧 */
  shifts: DoctorShift[];
  /** シフト位置情報のMap */
  positions: Map<string, TimelinePosition>;
  /** シフトバークリックハンドラー */
  onShiftClick: (shift: DoctorShift) => void;
  /** 空き領域ダブルクリックハンドラー */
  onDoubleClick?: (dateString: string, event: React.MouseEvent<HTMLDivElement>) => void;
  /** 読み取り専用モード */
  readOnly?: boolean;
}

/**
 * タイムライン行コンポーネント
 *
 * @description
 * 1日分のシフト行を表示する。
 * 日付ラベルと複数のシフトバーを含む。
 *
 * @param {TimelineRowProps} props - コンポーネントProps
 * @returns {React.ReactElement} タイムライン行
 *
 * @example
 * <TimelineRow
 *   dateLabel="1/13(月)"
 *   dateString="2025-01-13"
 *   shifts={dayShifts}
 *   positions={positionMap}
 *   onShiftClick={handleShiftClick}
 *   onDoubleClick={handleDoubleClick}
 * />
 */
const TimelineRow: React.FC<TimelineRowProps> = memo(
  ({ dateLabel, dateString, shifts, positions, onShiftClick, onDoubleClick, readOnly = false }) => {
    /**
     * ダブルクリックハンドラー
     */
    const handleDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (!readOnly && onDoubleClick) {
        onDoubleClick(dateString, event);
      }
    };

    return (
      <StyledTimelineRow>
        {/* 日付 */}
        <DoctorNameColumn>
          <Typography variant="body2" noWrap title={dateLabel}>
            {dateLabel}
          </Typography>
        </DoctorNameColumn>

        {/* シフトエリア */}
        <ShiftArea onDoubleClick={!readOnly ? handleDoubleClick : undefined}>
          {shifts.map((shift) => {
            const position = positions.get(shift.id);
            if (!position) return null;

            return (
              <ShiftBar
                key={shift.id}
                shift={shift}
                position={position}
                onClick={onShiftClick}
                readOnly={readOnly}
              />
            );
          })}
        </ShiftArea>
      </StyledTimelineRow>
    );
  }
);

TimelineRow.displayName = 'TimelineRow';

export { TimelineRow };
